// src/pages/ValmikiLab.jsx
// VALMIKI AI LAB — Lab page (Page 4 of the 6-page Phase 1 architecture)
// ONE self-contained file: Navbar + Footer + all CSS live here (same design system as ValmikiHome.jsx).
// Lead popup: <ValmikiLeadForm /> opens itself after its own timer (see that file).

import React, { useEffect, useRef, useState } from "react";
import ValmikiFooter from "./ValmikiFooter";
import ValmikiLeadForm from "./ValmikiLeadPopup";

/* ───────────────────────── CONFIG ───────────────────────── */

const HERO_IMAGE = "";
const HERO_IMAGE_ALT = "Students collaborating around a workbench in the VALMIKI AI LAB";
const LAB_IMAGE = "";
const LAB_IMAGE_ALT = "Students testing an AI workflow together during a lab session";

/* ───────────────────────── CONTENT ───────────────────────── */

const insideLab = [
  "Experiment with AI tools and workflows",
  "Translate concepts into practical use cases",
  "Build automation and intelligent workflows",
  "Solve structured business problems",
  "Document projects and portfolio evidence",
];

const labFocus = [
  { name: "Generative AI Lab", desc: "Research, productivity and problem-solving." },
  { name: "AI Automation Lab", desc: "Workflow design and automation." },
  { name: "Agentic AI Lab", desc: "Agent-based AI workflows." },
  { name: "Creative AI Lab", desc: "AI-assisted communication and creative applications." },
  { name: "AI Innovation Lab", desc: "Experiments, prototypes and student-led problem-solving." },
];

const principle = ["Experiment.", "Build.", "Test.", "Improve."];

const futureItems = ["Innovation initiatives", "Industry challenges", "Incubation-oriented programs"];

const css = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,400&display=swap');

.vl-root{
  --ink:#0B1626; --ink-2:#132238; --ink-3:#08111E;
  --paper:#F3F5F8; --white:#FFFFFF;
  --gold:#C8A24A; --gold-hi:#E0BC62; --gold-deep:#7D5F12;
  --slate:#4F5C73; --on-ink:#F3F5F8; --on-ink-mute:rgba(243,245,248,.72);
  --line:rgba(11,22,38,.14); --line-ink:rgba(243,245,248,.16);
  --serif:'Newsreader',Georgia,'Times New Roman',serif;
  --sans:'IBM Plex Sans','Segoe UI',Arial,sans-serif;
  --mono:'IBM Plex Mono',ui-monospace,Menlo,Consolas,monospace;
  font-family:var(--sans); color:var(--ink); background:var(--paper);
  line-height:1.6; -webkit-font-smoothing:antialiased; margin:0;
}
.vl-root *{box-sizing:border-box;}
:where(.vl-root) :where(h1,h2,h3,p,ul,ol){margin:0;padding:0;}
:where(.vl-root) :where(ul,ol){list-style:none;}
:where(.vl-root) a{color:inherit;}
.vl-root :focus-visible{outline:2px solid var(--gold-hi);outline-offset:3px;}

.vl-wrap{max-width:1120px;margin:0 auto;padding:0 24px;}
.vl-section{padding:104px 0;}
.vl-dark{background:var(--ink);color:var(--on-ink);}
.vl-white{background:var(--white);}

.vl-h2{font-family:var(--serif);font-weight:500;font-size:clamp(2rem,4vw,3rem);line-height:1.12;letter-spacing:-.015em;}
.vl-h3{font-family:var(--serif);font-weight:500;font-size:1.5rem;line-height:1.2;}
.vl-lead{font-size:1.125rem;line-height:1.65;max-width:56ch;}
.vl-muted{color:var(--slate);}
.vl-dark .vl-muted{color:var(--on-ink-mute);}
.vl-head{max-width:44rem;margin-bottom:56px;}
.vl-head .vl-h2{margin-bottom:16px;}

/* buttons */
@property --vl-a{syntax:"<angle>";inherits:false;initial-value:0deg;}
.vl-actions{display:flex;flex-wrap:wrap;gap:14px;}
.vl-btn{
  --fill:#162B47; --edge:var(--gold); --line:var(--gold);
  --wipe:var(--gold); --hover-text:var(--ink); --text:var(--on-ink);
  position:relative;overflow:hidden;isolation:isolate;
  display:inline-flex;align-items:center;justify-content:center;padding:14px 28px;border-radius:2px;
  font:600 15px/1 var(--sans);text-decoration:none;cursor:pointer;color:var(--text);
  border:1px solid var(--edge);
  background-color:var(--fill);
  background-image:linear-gradient(var(--wipe),var(--wipe));
  background-repeat:no-repeat;background-origin:border-box;background-position:left center;background-size:0% 100%;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s,border-color .3s ease;
}
.vl-btn::before{
  content:"";position:absolute;inset:0;padding:2px;border-radius:inherit;pointer-events:none;
  background:conic-gradient(from var(--vl-a),
    transparent 0deg,var(--line) 55deg,transparent 56deg,
    transparent 180deg,var(--line) 235deg,transparent 236deg);
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  -webkit-mask-composite:xor;
  mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  mask-composite:exclude;
  animation:vl-orbit 3.2s linear infinite;
}
@keyframes vl-orbit{to{--vl-a:360deg;}}
.vl-btn:hover,.vl-btn:focus-visible{background-size:100% 100%;color:var(--hover-text);border-color:var(--wipe);}
.vl-btn-gold{animation:vl-breathe 4s ease-in-out infinite;}
@keyframes vl-breathe{
  0%,100%{background-color:#162B47;box-shadow:0 6px 14px rgba(0,0,0,.35);}
  50%{background-color:#24426B;box-shadow:0 6px 18px rgba(200,162,74,.30);}
}
.vl-btn-line-light{--fill:transparent;--edge:var(--paper);--line:var(--paper);--wipe:var(--paper);--hover-text:var(--ink);}
.vl-btn-ink{--fill:var(--ink);--edge:var(--gold);--line:var(--gold);--wipe:var(--gold);--hover-text:var(--ink);--text:var(--paper);}
.vl-btn-line-ink{--fill:transparent;--edge:var(--ink);--line:var(--ink);--wipe:var(--ink);--hover-text:var(--paper);--text:var(--ink);}
.vl-final .vl-btn-ink{--edge:var(--paper);--line:var(--paper);--wipe:var(--paper);}
@media (prefers-reduced-motion:reduce){
  .vl-btn,.vl-toggle{transition:none;}
  .vl-btn::before{animation:none;}
  .vl-btn-gold{animation:none;}
}

/* header */
.vl-header{position:sticky;top:0;z-index:50;background:var(--ink);border-bottom:1px solid var(--line-ink);}
.vl-header-row{display:flex;align-items:center;justify-content:space-between;min-height:68px;gap:24px;}
.vl-brand{display:flex;align-items:center;gap:12px;text-decoration:none;color:var(--on-ink);
  font:600 17px/1 var(--sans);letter-spacing:.14em;}
.vl-nav{display:flex;align-items:center;gap:26px;}
.vl-nav a:not(.vl-btn){font-size:12.5px;font-weight:500;letter-spacing:.09em;text-decoration:none;color:var(--on-ink-mute);
  padding:6px 0;border-bottom:1.5px solid transparent;transition:color .15s,border-color .15s;}
.vl-nav a:not(.vl-btn):hover{color:var(--on-ink);}
.vl-nav a[aria-current="page"]{color:var(--on-ink);border-bottom-color:var(--gold);}
.vl-nav .vl-btn{padding:11px 20px;font-size:12.5px;letter-spacing:.09em;}
.vl-toggle{display:none;background-color:transparent;
  background-image:linear-gradient(var(--paper),var(--paper));background-repeat:no-repeat;
  background-origin:border-box;background-position:left center;background-size:0% 100%;
  border:1px solid var(--line-ink);color:var(--on-ink);
  width:42px;height:42px;border-radius:3px;font-size:20px;cursor:pointer;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s;}
.vl-toggle:hover,.vl-toggle:focus-visible{background-size:100% 100%;color:var(--ink);}
.vl-mobile{display:none;background:var(--ink);border-top:1px solid var(--line-ink);padding:8px 24px 22px;}
.vl-mobile a:not(.vl-btn){display:block;padding:14px 0;border-bottom:1px solid var(--line-ink);text-decoration:none;
  font-size:14px;letter-spacing:.08em;color:var(--on-ink);}
.vl-mobile .vl-btn{margin-top:18px;width:100%;}
@media (max-width:1000px){
  .vl-nav{display:none;}
  .vl-toggle{display:block;}
  .vl-mobile.open{display:block;}
}

/* ───────── hero ───────── */
.vl-hero{position:relative;overflow:hidden;background-color:var(--ink);color:var(--on-ink);padding:88px 0 104px;
  background-image:linear-gradient(rgba(243,245,248,.035) 1px,transparent 1px),
                   linear-gradient(90deg,rgba(243,245,248,.035) 1px,transparent 1px);
  background-size:56px 56px;}
.vl-hero-grid{position:relative;z-index:1;display:grid;grid-template-columns:1.05fr .95fr;gap:64px;align-items:center;}
.vl-h1{font-family:var(--serif);font-weight:500;font-size:clamp(2.4rem,5vw,4rem);line-height:1.05;
  letter-spacing:-.025em;margin-bottom:24px;}
.vl-hero .vl-lead{color:var(--on-ink-mute);margin-bottom:36px;}
.vl-hero-fx{position:absolute;inset:0;pointer-events:none;overflow:hidden;}
.vl-hero-glow{position:absolute;right:-6%;top:6%;width:560px;height:560px;border-radius:50%;
  background:radial-gradient(circle,rgba(200,162,74,.18),transparent 65%);}

.vl-visual{position:relative;}
.vl-photo{display:block;width:100%;aspect-ratio:4/3;object-fit:cover;border-radius:10px;}
.vl-loop{background:var(--ink-2);border:1px solid var(--line-ink);border-radius:10px;padding:22px 24px 12px;
  box-shadow:0 24px 60px rgba(0,0,0,.35);}
.vl-visual.has-photo .vl-loop{position:relative;margin:-64px 0 0 -28px;width:calc(100% - 20px);padding-bottom:6px;}
.vl-loop-head{display:flex;justify-content:space-between;align-items:baseline;gap:12px;
  padding-bottom:14px;border-bottom:1px solid var(--line-ink);}
.vl-loop-title{font:600 15px/1.3 var(--sans);}
.vl-loop-tag{font:400 12px/1 var(--mono);color:var(--on-ink-mute);}
.vl-loop svg{display:block;width:100%;height:auto;max-width:420px;margin:0 auto;}
.vl-ring-track{fill:none;stroke:var(--line-ink);stroke-width:1.5;stroke-dasharray:3 6;}
.vl-ring-node{fill:var(--ink-2);stroke:var(--gold);stroke-width:1.6;}
.vl-ring-label{font-family:var(--serif);font-size:19px;fill:var(--on-ink);}
.vl-ring-center{font:400 12px var(--mono);fill:var(--on-ink-mute);}
.vl-ring-dot{fill:var(--gold-hi);}
.vl-ring-halo{fill:rgba(224,188,98,.25);}
.vl-ring-spin{transform-origin:210px 170px;transform-box:view-box;}

@media (prefers-reduced-motion:no-preference){
  .vl-hero{animation:vl-grid 10s linear infinite;}
  .vl-hero-glow{animation:vl-glow 4s ease-in-out infinite alternate;}
  .vl-h1,.vl-hero .vl-lead,.vl-hero .vl-actions{animation:vl-rise .5s cubic-bezier(.2,.7,.2,1) both;}
  .vl-h1{animation-delay:.05s;}
  .vl-hero .vl-lead{animation-delay:.2s;}
  .vl-hero .vl-actions{animation-delay:.35s;}
  .vl-loop{animation:vl-panel-in .55s cubic-bezier(.2,.7,.2,1) .1s both;}
  .vl-hero .vl-btn::before{animation-duration:2s;}
  .vl-hero .vl-btn-gold{animation-duration:2.5s;}
  /* the loop: one full turn every 8s, so each stage is reached every 2s */
  .vl-ring-spin{animation:vl-spin 8s linear 1s infinite;}
  .vl-ring-node{animation:vl-stage 8s ease-in-out infinite;animation-delay:calc(1s + var(--i) * 2s);}
  .vl-ring-label{animation:vl-label 8s ease-in-out infinite;animation-delay:calc(1s + var(--i) * 2s);}
}
@keyframes vl-grid{to{background-position:56px 56px,56px 56px;}}
@keyframes vl-glow{to{transform:translate(-50px,40px) scale(1.12);}}
@keyframes vl-rise{from{opacity:0;transform:translateY(22px);}to{opacity:1;transform:none;}}
@keyframes vl-panel-in{from{opacity:0;transform:translateX(28px);}to{opacity:1;transform:none;}}
@keyframes vl-spin{to{transform:rotate(360deg);}}
@keyframes vl-stage{
  0%{fill:var(--gold);stroke:var(--gold-hi);}
  18%{fill:var(--gold);stroke:var(--gold-hi);}
  32%,100%{fill:var(--ink-2);stroke:var(--gold);}
}
@keyframes vl-label{
  0%,18%{fill:var(--gold-hi);}
  32%,100%{fill:var(--on-ink);}
}

/* ───────── what happens inside the lab ───────── */
.vl-split{display:grid;grid-template-columns:.9fr 1.1fr;gap:72px;align-items:start;}
.vl-split .vl-h2{margin-bottom:24px;}
.vl-inside li{position:relative;padding:22px 0 22px;border-top:1px solid var(--line);
  font-family:var(--serif);font-size:1.5rem;line-height:1.3;}
.vl-inside li:last-child{border-bottom:1px solid var(--line);}
.vl-inside li::after{content:"";position:absolute;top:-1px;left:0;right:0;height:2px;background:var(--gold);transform-origin:left;}
@media (prefers-reduced-motion:no-preference){
  .vl-inside li::after{transition:transform .6s cubic-bezier(.5,0,.3,1) calc(var(--i) * .45s + .1s);}
  .vl-inside li{transition:opacity .5s ease calc(var(--i) * .45s + .1s);}
  .vl-inside:not(.in) li::after{transform:scaleX(0);}
  .vl-inside:not(.in) li{opacity:.3;}
}

/* optional photo band */
.vl-band{margin:0;}
.vl-band img{display:block;width:100%;max-height:520px;object-fit:cover;}

/* ───────── lab focus areas ───────── */
.vl-focus{border-top:1px solid var(--line-ink);}
.vl-focus li{position:relative;display:grid;grid-template-columns:1fr 1fr;gap:40px;align-items:baseline;
  padding:30px 0 30px 0;border-bottom:1px solid var(--line-ink);
  background-image:linear-gradient(var(--gold),var(--gold));background-repeat:no-repeat;
  background-position:left center;background-size:3px 0%;transition:background-size .3s ease,padding-left .3s ease;}
.vl-focus li:hover{background-size:3px 60%;padding-left:20px;}
.vl-focus-name{font-family:var(--serif);font-size:clamp(1.6rem,3vw,2.25rem);line-height:1.15;letter-spacing:-.01em;}
.vl-focus-desc{font-size:16px;color:var(--on-ink-mute);max-width:40ch;}
@media (prefers-reduced-motion:reduce){.vl-focus li{transition:none;}}

/* ───────── principle ───────── */
.vl-principle-wrap{display:grid;grid-template-columns:auto 1fr;gap:72px;align-items:center;}
.vl-principle-label{font-size:14px;font-weight:600;color:var(--gold-deep);}
.vl-principle{font-family:var(--serif);font-weight:500;font-size:clamp(3rem,8vw,6.5rem);line-height:.98;letter-spacing:-.03em;}
.vl-principle span{display:block;}
.vl-principle-side{max-width:34ch;}
.vl-principle-side p{margin-top:12px;}
@media (prefers-reduced-motion:no-preference){
  .vl-principle span{animation:vl-word 8s ease-in-out infinite;animation-delay:calc(var(--i) * 2s);}
}
@keyframes vl-word{
  0%,18%{color:var(--gold-deep);}
  32%,100%{color:var(--ink);}
}

/* ───────── future direction ───────── */
.vl-future-list{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;margin-top:44px;}
.vl-future-list li{padding:26px 24px;border:1.5px dashed var(--gold);border-radius:2px;
  font-family:var(--serif);font-size:1.35rem;line-height:1.25;}
.vl-future-note{margin-top:20px;font-size:14px;color:var(--on-ink-mute);}

/* ───────── final cta ───────── */
.vl-final{background:var(--gold);color:var(--ink);padding:96px 0;}
.vl-final .vl-h2{font-size:clamp(2.1rem,4.6vw,3.6rem);max-width:19em;margin-bottom:40px;}

/* footer */
.vl-footer{background:var(--ink-3);color:var(--on-ink);padding:56px 0 40px;}
.vl-footer-row{display:flex;flex-wrap:wrap;justify-content:space-between;gap:32px;padding-bottom:36px;border-bottom:1px solid var(--line-ink);}
.vl-footer p{font-size:14px;color:var(--on-ink-mute);max-width:38ch;margin-top:14px;}
.vl-footer nav{display:flex;flex-wrap:wrap;gap:12px 28px;align-items:flex-start;}
.vl-footer nav a{font-size:13px;letter-spacing:.08em;text-decoration:none;color:var(--on-ink-mute);}
.vl-footer nav a:hover{color:var(--on-ink);}
.vl-footer .vl-copy{padding-top:24px;margin-top:0;max-width:none;font-size:13px;color:var(--on-ink-mute);}

/* responsive */
@media (max-width:860px){
  .vl-section{padding:72px 0;}
  .vl-hero{padding:56px 0 72px;}
  .vl-hero-grid,.vl-split,.vl-principle-wrap{grid-template-columns:1fr;gap:48px;}
  .vl-visual.has-photo .vl-loop{margin:-40px 0 0 0;width:calc(100% - 24px);}
  .vl-focus li{grid-template-columns:1fr;gap:8px;}
  .vl-future-list{grid-template-columns:1fr;}
}
@media (max-width:560px){
  .vl-wrap{padding:0 20px;}
  .vl-actions .vl-btn{width:100%;}
}
`;

function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, seen];
}

const LOOP_NODES = [
  { label: "Experiment", cx: 210, cy: 70, lx: 210, ly: 40, anchor: "middle" },
  { label: "Build", cx: 310, cy: 170, lx: 334, ly: 176, anchor: "start" },
  { label: "Test", cx: 210, cy: 270, lx: 210, ly: 312, anchor: "middle" },
  { label: "Improve", cx: 110, cy: 170, lx: 86, ly: 176, anchor: "end" },
];

function LabLoop() {
  return (
    <svg viewBox="0 0 420 340" role="img" aria-label="The lab cycle: Experiment, Build, Test, Improve, then repeat">
      <circle className="vl-ring-track" cx="210" cy="170" r="100" />
      <g className="vl-ring-spin" aria-hidden="true">
        <circle className="vl-ring-halo" cx="210" cy="70" r="11" />
        <circle className="vl-ring-dot" cx="210" cy="70" r="5" />
      </g>
      {LOOP_NODES.map((n, i) => (
        <g key={n.label} style={{ "--i": i }}>
          <circle className="vl-ring-node" cx={n.cx} cy={n.cy} r="9" style={{ "--i": i }} />
          <text className="vl-ring-label" x={n.lx} y={n.ly} textAnchor={n.anchor} style={{ "--i": i }}>
            {n.label}
          </text>
        </g>
      ))}
      <text className="vl-ring-center" x="210" y="166" textAnchor="middle">repeat</text>
      <text className="vl-ring-center" x="210" y="184" textAnchor="middle">and improve</text>
    </svg>
  );
}

const VALMIKI_ROUTES = {
  home: "/valmiki",
  about: "/valmiki/about",
  program: "/valmiki/program",
  lab: "/valmiki/lab",
  partner: "/valmiki/partner",
  contact: "/valmiki/contact",
};

const VALMIKI_NAV_LINKS = [
  { label: "HOME", href: VALMIKI_ROUTES.home },
  { label: "ABOUT VALMIKI", href: VALMIKI_ROUTES.about },
  { label: "PROGRAM", href: VALMIKI_ROUTES.program },
  { label: "VALMIKI AI LAB", href: VALMIKI_ROUTES.lab, current: true },
  { label: "CONTACT", href: VALMIKI_ROUTES.contact },
];

const ValmikiBrandMark = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true">
    <circle cx="15" cy="15" r="14" stroke="#C8A24A" strokeWidth="1.2" />
    <path d="M9 19L15 8L21 19" stroke="#F3F5F8" strokeWidth="1.4" strokeLinejoin="round" />
    <circle cx="15" cy="15.5" r="1.6" fill="#C8A24A" />
  </svg>
);

export default function ValmikiLab() {
  const [insideRef, insideSeen] = useInView(0.35);

  return (
    <div className="vl-root" id="top">
      <style>{css}</style>

      <main>
        {/* HERO */}
        <section className="vl-hero" aria-labelledby="vl-lab-title">
          <div className="vl-hero-fx" aria-hidden="true"><span className="vl-hero-glow" /></div>
          <div className="vl-wrap vl-hero-grid">
            <div>
              <h1 className="vl-h1" id="vl-lab-title">The VALMIKI AI LAB</h1>
              <p className="vl-lead">
                Where students experiment, build and explore the practical possibilities of Artificial Intelligence.
              </p>
              <div className="vl-actions">
                <a className="vl-btn vl-btn-gold" href="#vl-inside">See What Happens Inside</a>
                <a className="vl-btn vl-btn-line-light" href={VALMIKI_ROUTES.program}>Explore the Program</a>
              </div>
            </div>

            <div className={`vl-visual${HERO_IMAGE ? " has-photo" : ""}`}>
              {HERO_IMAGE && <img className="vl-photo" src={HERO_IMAGE} alt={HERO_IMAGE_ALT} />}
              <aside className="vl-loop" aria-label="The lab cycle">
                <div className="vl-loop-head">
                  <span className="vl-loop-title">Our principle in motion</span>
                  <span className="vl-loop-tag">repeat</span>
                </div>
                <LabLoop />
              </aside>
            </div>
          </div>
        </section>

        {/* WHAT HAPPENS INSIDE THE LAB */}
        <section className="vl-section" id="vl-inside" aria-labelledby="vl-inside-title">
          <div className="vl-wrap vl-split">
            <div>
              <h2 className="vl-h2" id="vl-inside-title">What happens inside the lab?</h2>
              <p className="vl-lead vl-muted">
                Students work hands-on, together, and leave every session with something built.
              </p>
            </div>
            <ul ref={insideRef} className={`vl-inside${insideSeen ? " in" : ""}`}>
              {insideLab.map((item, i) => (
                <li key={item} style={{ "--i": i }}>{item}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* OPTIONAL PHOTO BAND (shown only when LAB_IMAGE is set) */}
        {LAB_IMAGE && (
          <figure className="vl-band">
            <img src={LAB_IMAGE} alt={LAB_IMAGE_ALT} />
          </figure>
        )}

        {/* LAB FOCUS AREAS */}
        <section className="vl-section vl-dark" aria-labelledby="vl-focus-title">
          <div className="vl-wrap">
            <div className="vl-head">
              <h2 className="vl-h2" id="vl-focus-title">Lab focus areas</h2>
            </div>
            <ul className="vl-focus">
              {labFocus.map((f) => (
                <li key={f.name}>
                  <h3 className="vl-focus-name">{f.name}</h3>
                  <p className="vl-focus-desc">{f.desc}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* OUR PRINCIPLE */}
        <section className="vl-section vl-white" aria-labelledby="vl-principle-title">
          <div className="vl-wrap vl-principle-wrap">
            <p className="vl-principle" aria-label="Experiment. Build. Test. Improve.">
              {principle.map((w, i) => (
                <span key={w} style={{ "--i": i }} aria-hidden="true">{w}</span>
              ))}
            </p>
            <div className="vl-principle-side">
              <h2 className="vl-h3" id="vl-principle-title">Our principle</h2>
              <p className="vl-muted">
                Every lab session runs on this loop: try an idea, build it, test it, then make it better.
              </p>
            </div>
          </div>
        </section>

        {/* FUTURE DIRECTION */}
        <section className="vl-section vl-dark" aria-labelledby="vl-future-title">
          <div className="vl-wrap">
            <div className="vl-head" style={{ marginBottom: 0 }}>
              <h2 className="vl-h2" id="vl-future-title">Future direction</h2>
              <p className="vl-lead vl-muted">
                As the ecosystem matures, VALMIKI may expand selected innovation initiatives, industry
                challenges and incubation-oriented programs based on capacity and partnerships.
              </p>
            </div>
            <ul className="vl-future-list" aria-label="Areas VALMIKI may expand into">
              {futureItems.map((f) => <li key={f}>{f}</li>)}
            </ul>
            <p className="vl-future-note">Planned as possibilities, subject to capacity and partnerships.</p>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="vl-final" aria-labelledby="vl-final-title">
          <div className="vl-wrap">
            <h2 className="vl-h2" id="vl-final-title">
              Bring your ideas to the lab. Leave with something you built.
            </h2>
            <div className="vl-actions">
              <a className="vl-btn vl-btn-ink" href={VALMIKI_ROUTES.program}>Explore the Program</a>
              <a className="vl-btn vl-btn-line-ink" href={VALMIKI_ROUTES.partner}>Partner With Us</a>
            </div>
          </div>
        </section>
      </main>

      <ValmikiFooter />

      {/* Lead popup — ValmikiLeadForm opens itself after OPEN_AFTER_MS (see that file) */}
      <ValmikiLeadForm />
    </div>
  );
}